import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router, RouterModule } from "@angular/router";
import { trigger, transition, style, animate, state } from "@angular/animations";
import { Subscription } from "rxjs";
import { LanguageService } from "../core/services/language.service";

type ManageCard = {
  id: string;
  titleKey: string;
  image: string;
  route?: string[];
};

@Component({
  selector: "manage-senior-center-component",
  standalone: true,
  templateUrl: './manage-senior-center.component.html',
  styleUrls: ['./programs.component.css'],
  imports: [CommonModule, RouterModule],
  animations: [
    trigger('pageTransition', [
      state('hidden', style({ opacity: 0, visibility: 'hidden' })),
      state('visible', style({ opacity: 1, visibility: 'visible' })),
      transition('hidden => visible', [animate('400ms ease-out')]),
    ]),
    trigger('cardClick', [
      transition('* => clicked', [
        animate('300ms ease-in-out', style({ opacity: 0, transform: 'scale(0.95)' })),
      ]),
    ]),
  ],
})
export class ManageSeniorCenterComponent implements OnInit, OnDestroy {
  private readonly languageService = inject(LanguageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private languageSub?: Subscription;

  isLoading = true;
  clickedCard: string | null = null;
  imagesLoaded: Record<string, boolean> = {};
  pageTransitionState: 'hidden' | 'visible' = 'visible';
  private isRedirecting = false;

  private readonly cardDefs: ManageCard[] = [
    {
      id: 'activity',
      titleKey: 'manageSenior.card.activity',
      image: 'assets/images/manage-activity.png',
      route: ['/manage-services/senior-center/activity'],
    },
    {
      id: 'meal-menu',
      titleKey: 'manageSenior.card.mealMenu',
      image: 'assets/images/manage-meal-menu.png',
      route: ['/manage-services/senior-center/meal-menu'],
    },
    {
      id: 'meal-report',
      titleKey: 'manageSenior.card.report',
      image: 'assets/images/manage-meal-report.png',
      route: ['/manage-services/senior-center/reports'],
    },
  ];

  constructor(private readonly router: Router) {}

  get loadingLabel(): string {
    return this.languageService.t('programs.loading');
  }

  get pageTitle(): string {
    return this.languageService.t('manageSenior.title');
  }

  get backLabel(): string {
    return this.languageService.t('manageSenior.back');
  }

  get cards(): ManageCard[] {
    return this.cardDefs;
  }

  trackByCardId(_index: number, card: ManageCard): string {
    return card.id;
  }

  cardTitle(card: ManageCard): string {
    return this.languageService.t(card.titleKey);
  }

  ngOnInit(): void {
    this.languageSub = this.languageService.language$.subscribe(() => this.cdr.markForCheck());

    const token = sessionStorage.getItem('token');
    const staffInfo = sessionStorage.getItem('staffInfo');
    if (!token || !staffInfo) {
      this.redirectToSignIn(this.languageService.t('programs.signInRequired'));
      return;
    }

    this.cardDefs.forEach((card) => {
      this.imagesLoaded[card.id] = false;
    });

    setTimeout(() => {
      this.isLoading = false;
      this.cdr.markForCheck();
    }, 150);
  }

  ngOnDestroy(): void {
    this.languageSub?.unsubscribe();
  }

  goBack(): void {
    this.router.navigate(['/programs'], { queryParams: { tab: 'manage-services' } });
  }

  onCardClick(card: ManageCard, event: Event): void {
    event.preventDefault();
    this.clickedCard = card.id;
    setTimeout(() => {
      if (card.route?.length) {
        void this.router.navigate(card.route);
      } else {
        this.clickedCard = null;
      }
    }, 300);
  }

  onImageLoad(cardId: string): void {
    this.imagesLoaded[cardId] = true;
  }

  onImageError(cardId: string): void {
    this.imagesLoaded[cardId] = true;
  }

  private redirectToSignIn(errorMessage?: string): void {
    if (this.isRedirecting) {
      return;
    }
    this.isRedirecting = true;
    this.isLoading = true;
    setTimeout(() => {
      this.router.navigate(['/'], {
        replaceUrl: true,
        queryParams: errorMessage ? { error: encodeURIComponent(errorMessage) } : undefined,
      });
    }, 0);
  }
}
